import { useId, useRef, useState } from 'react';
import {
  parseProductionLineBriefFields,
  ProductionLineBriefInputError,
} from '@mouja/domain/production-line-brief';
import { useLanguage } from '../../i18n/language.js';
import { Button, Field, Modal } from '../../merchant/ui/primitives.js';
import { newIdempotencyKey } from '../../workspace/api.js';
import { saveBrief, type SavedBrief } from './savedBriefApi.js';
const copy = {
  en: {
    new: 'Start a video',
    edit: 'Edit brief',
    duplicate: 'Duplicate project',
    body: 'Starts from your approved Business Profile — no need to re-enter your website.',
    editBody:
      'Save a new version and restart research for this line. Earlier work stays in history.',
    about: 'What is this line about?',
    instructions: 'Any instructions or references? (optional)',
    name: 'Project name (optional)',
    locale: 'Content language',
    languageHint: 'Independent of the interface language.',
    submit: 'Create project',
    save: 'Save brief',
    saving: 'Saving…',
    cancel: 'Cancel',
    required: 'Describe what this project is about.',
    invalid: 'Check the length of this field.',
    failed:
      'Could not save. Your text is kept. Check the connection or reload the latest brief before retrying.',
  },
  ar: {
    new: 'ابدأ فيديو',
    edit: 'تعديل الملخص',
    duplicate: 'نسخ المشروع',
    body: 'يبدأ من ملفك التجاري المعتمد — لا حاجة لإعادة إدخال موقعك.',
    editBody: 'احفظ إصداراً جديداً وابدأ البحث لهذا الخط. يبقى العمل السابق في السجل.',
    about: 'ما موضوع هذا الخط؟',
    instructions: 'أي تعليمات أو مراجع؟ (اختياري)',
    name: 'اسم المشروع (اختياري)',
    locale: 'لغة المحتوى',
    languageHint: 'مستقلة عن لغة الواجهة.',
    submit: 'إنشاء المشروع',
    save: 'حفظ الملخص',
    saving: 'جارٍ الحفظ…',
    cancel: 'إلغاء',
    required: 'صف موضوع المشروع.',
    invalid: 'تحقق من طول هذا الحقل.',
    failed:
      'تعذّر الحفظ. احتفظنا بالنص. تحقق من الاتصال أو أعد تحميل أحدث ملخص قبل المحاولة.',
  },
};
export function SavedBriefForm({
  onClose,
  onCreated,
  source,
  existing,
  mode = 'create',
}: {
  onClose: () => void;
  onCreated: (lineId: string) => void;
  source?: { sourceIntakeId: string; expectedApprovedProfileArtifactId: string };
  existing?: SavedBrief;
  mode?: 'create' | 'edit' | 'duplicate';
}) {
  const { language } = useLanguage(),
    ui = copy[language],
    prefix = useId();
  const [about, setAbout] = useState(existing?.about ?? ''),
    [instructions, setInstructions] = useState(existing?.instructions ?? '');
  const [title, setTitle] = useState(existing?.title ?? ''),
    [locale, setLocale] = useState<'en' | 'ar'>(existing?.outputLocale ?? language);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [invalid, setInvalid] = useState('');
  const intent = useRef<{ fingerprint: string; key: string } | null>(null);
  async function submit() {
    setInvalid('');
    setError('');
    let fields;
    try {
      fields = parseProductionLineBriefFields({
        about,
        instructions,
        title,
        outputLocale: locale,
      });
    } catch (e) {
      setInvalid(e instanceof ProductionLineBriefInputError ? e.field : 'about');
      return;
    }
    const body =
      mode === 'create'
        ? { ...source, ...fields }
        : {
            productionLineId: existing?.productionLineId,
            expectedVersionId: existing?.versionId,
            ...fields,
          };
    const fingerprint = JSON.stringify(body);
    if (intent.current?.fingerprint !== fingerprint)
      intent.current = { fingerprint, key: newIdempotencyKey() };
    setBusy(true);
    try {
      const saved = await saveBrief(mode, body, intent.current.key);
      onCreated(saved.productionLineId);
    } catch {
      setError(ui.failed);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title={mode === 'create' ? ui.new : ui[mode]}
      description={mode === 'edit' ? ui.editBody : ui.body}
      onClose={() => {
        if (!busy) onClose();
      }}
      onSubmit={() => void submit()}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {ui.cancel}
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={busy}
            aria-busy={busy}
            pixelWave
          >
            {busy ? ui.saving : mode === 'edit' ? ui.save : ui.submit}
          </Button>
        </>
      }
    >
      {error && <p role="alert">{error}</p>}
      <Field
        label={ui.about}
        id={`${prefix}-about`}
        error={invalid === 'about' ? ui.required : null}
      >
        <textarea
          className="mj-ws-inp"
          id={`${prefix}-about`}
          value={about}
          rows={4}
          dir="auto"
          data-autofocus="1"
          aria-invalid={invalid === 'about'}
          onChange={(e) => {
            setAbout(e.target.value);
          }}
          disabled={busy}
        />
      </Field>
      <Field
        label={ui.instructions}
        id={`${prefix}-instructions`}
        error={invalid === 'instructions' ? ui.invalid : null}
      >
        <textarea
          className="mj-ws-inp"
          id={`${prefix}-instructions`}
          value={instructions}
          rows={3}
          dir="auto"
          aria-invalid={invalid === 'instructions'}
          onChange={(e) => {
            setInstructions(e.target.value);
          }}
          disabled={busy}
        />
      </Field>
      <Field
        label={ui.name}
        id={`${prefix}-title`}
        error={invalid === 'title' ? ui.invalid : null}
      >
        <input
          className="mj-ws-inp"
          id={`${prefix}-title`}
          value={title}
          dir="auto"
          placeholder={Array.from(about.trim()).slice(0, 80).join('')}
          aria-invalid={invalid === 'title'}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
          disabled={busy}
        />
      </Field>
      <Field label={ui.locale} id={`${prefix}-locale`}>
        <select
          className="mj-ws-inp"
          id={`${prefix}-locale`}
          value={locale}
          disabled={busy}
          onChange={(e) => {
            setLocale(e.target.value === 'ar' ? 'ar' : 'en');
          }}
        >
          <option value="en">English</option>
          <option value="ar">العربية</option>
        </select>
        <p className="mj-ws-muted mj-ws-sm">{ui.languageHint}</p>
      </Field>
    </Modal>
  );
}
